
import React from "react";
import { SessionEventComponent } from "./SessionEvent.tsx";
import { isSameDay, startOfDay } from "date-fns";

interface CalendarDayProps {
  date: Date;
  events: any[];
  onEventClick: (event: any) => void;
  onAddSession?: (date: Date) => void;
  isToday?: boolean;
  isCurrentMonth?: boolean;
  maxVisibleEvents?: number;
  variations?: any[];
  /** First visible day in the month block (grid start; often a Sunday). */
  monthGridStart: Date;
}

export const CalendarDay: React.FC<CalendarDayProps> = ({
  date,
  events,
  onEventClick,
  onAddSession,
  isToday = false,
  isCurrentMonth = true,
  maxVisibleEvents = 6,
  monthGridStart,
}) => {
  const dayNumber = date.getDate();
  
  // Sort events by the specified criteria:
  // 1. Primary order by numDaysCalc DESC
  // 2. Secondary order by regularProgrammingStart ASC  
  // 3. Tertiary order by title alphabetical from A to Z
  const sortedEvents = [...events].sort((a, b) => {
    // Primary: numDaysCalc DESC (higher values first)
    const numDaysA = a.numDaysCalc || 0;
    const numDaysB = b.numDaysCalc || 0;
    if (numDaysA !== numDaysB) {
      return numDaysB - numDaysA; // DESC order
    }
    
    // Secondary: regularProgrammingStart ASC (earlier times first)
    const startA = a.regularProgrammingStart || 0;
    const startB = b.regularProgrammingStart || 0;
    if (startA !== startB) {
      return startA - startB; // ASC order
    }
    
    // Tertiary: title alphabetical A to Z
    const titleA = a.title || "";
    const titleB = b.title || "";
    return titleA.localeCompare(titleB);
  });

  // Show only up to maxVisibleEvents
  const visibleEvents = sortedEvents.slice(0, maxVisibleEvents);
  const hasMoreEvents = sortedEvents.length > maxVisibleEvents;

  return (
    <div className={`min-h-[120px] border border-gray-200 p-1 ${isToday ? 'bg-blue-50' : 'bg-white'} ${!isCurrentMonth ? 'bg-gray-50 text-gray-400' : ''}`}>
      <div className="flex justify-between items-center mb-1">
        <span className={`text-sm font-medium ${isToday ? 'text-blue-600' : isCurrentMonth ? 'text-gray-700' : 'text-gray-400'}`}>
          {dayNumber}
        </span>
      </div>
      
      <div className="space-y-1">
        {visibleEvents.map((event) => {
          // Normalize to date-only comparisons so UI remains correct even when
          // stored offer dates are pinned to a non-midnight time (e.g. 8:00 AM ET).
          const day = startOfDay(date);
          const eventStart = startOfDay(event.startDate);
          const eventEnd = startOfDay(event.endDate);

          const isMultiDay = !isSameDay(eventStart, eventEnd);
          const isStart = isSameDay(eventStart, day);
          const isEnd = isSameDay(eventEnd, day);

          // Improve readability: for events that started before this month, show
          // the title on the first day shown in the month block (grid start),
          // even when that day is in the previous month (e.g. Jul 27 for Aug 2026).
          const isMonthBlockAnchorDay = isSameDay(day, startOfDay(monthGridStart));
          const isDisplayStart = isStart || (isMultiDay && isMonthBlockAnchorDay && eventStart < day);
          
          return (
            <SessionEventComponent
              key={event.id}
              event={event}
              isMultiDay={isMultiDay}
              isStart={isDisplayStart}
              isEnd={isEnd}
              onClick={onEventClick}
            />
          );
        })}
        {hasMoreEvents && (
          <div className="text-xs text-gray-500 px-1">
            +{sortedEvents.length - maxVisibleEvents} more
          </div>
        )}
      </div>
    </div>
  );
};
